import { resetAllStores } from '@/resetStores.js'

import { useAuthStore } from '@/features/auth/useAuthStore.js'
import { createNoAccessPath } from '@/features/noAccess/noAccess.utils.js'
import { decidePermission } from '@/features/permissions/permissionEngine.js'
import { logPermissionDecision } from '@/features/permissions/permissionDiagnostics.js'
import { finishClubImpersonation } from '@/features/router/useActiveClub.js'

import { isSuperAdmin, useSuperAdminIdentity } from './useIsSuperAdmin'

function getPermissionActions(to) {
  const matched = [...(to.matched ?? [])].reverse()
  const metaWithAction = matched.find(
    record => record.meta?.permissionAction || record.meta?.permissionAnyActions,
  )?.meta

  if (!metaWithAction) return []
  if (metaWithAction.permissionAction) return [metaWithAction.permissionAction]
  return metaWithAction.permissionAnyActions ?? []
}

// if we go /super-admin, finish impersonation always
export async function superAdminGuard(to, from, next) {
  const authStore = useAuthStore()
  const _isSuperAdmin = isSuperAdmin(authStore.myDetails)
  const { isFullSuperAdmin } = useSuperAdminIdentity()

  if (_isSuperAdmin) {
    finishClubImpersonation()
    resetAllStores()

    if (!isFullSuperAdmin.value) {
      if (to.matched?.some(record => record.meta?.fullSuperAdminOnly)) {
        next('/no-access?reason=full-super-admin-only')
        return
      }

      const permissionActions = getPermissionActions(to)
      if (!permissionActions.length) {
        next()
        return
      }

      const decisions = permissionActions.map(action =>
        decidePermission(action, {
          myDetails: authStore.myDetails,
          surface: 'super-admin',
          route: to.fullPath,
        }),
      )

      if (decisions.some(decision => decision.allowed)) {
        next()
        return
      }

      const deniedDecision = decisions[0]
      logPermissionDecision('route_denied', deniedDecision, {
        route: to.fullPath,
      })
      next(
        createNoAccessPath({
          reason: 'permission-denied',
          action: deniedDecision?.action ?? 'super-admin-limited',
          returnUrl: to.fullPath,
        }),
      )
      return
    }

    next()
    return
  }

  next('/no-access?reason=not-super-admin')
}
